import React from 'react';
import { CommandManager, createChainableState, Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Fragment, Slice } from 'prosemirror-model';
import { Plugin, TextSelection } from 'prosemirror-state';
import ExternalLink from '../../../components/ExternalLink';
import { externalLinkSegments, normalizeExternalUrl } from '../../../utils/externalLinkText';

function ExternalLinkView({ node, deleteNode, editor }) {
  const remove = () => {
    if (!editor.isEditable || editor.isDestroyed) return;
    deleteNode();
    // Removing the focused node also removes its button. Return the caret to
    // the mapped document selection in the same user gesture.
    editor.view.focus();
  };
  return <NodeViewWrapper as="span" contentEditable={false} data-external-link-node="" data-no-swipe>
    <ExternalLink href={node.attrs.href} label={node.attrs.label} editing onRemove={remove} />
  </NodeViewWrapper>;
}

// Plain clipboard URLs become atoms after the HTML parser has preserved hrefs
// of labelled anchors. Existing text marks, images and paragraph boundaries
// survive the same transaction and therefore also survive Undo.
function linkifyFragment(fragment, type, schema, withinCode = false) {
  const nodes = [];
  fragment.forEach(node => {
    if (node.isText && !withinCode && !node.marks.some(mark => mark.type.name === 'code')) {
      externalLinkSegments(node.text).forEach(segment => {
        if (segment.href) nodes.push(type.create({ href: segment.href, label: segment.text }, null, node.marks));
        else if (segment.text) nodes.push(schema.text(segment.text, node.marks));
      });
    } else if (!node.isLeaf) {
      nodes.push(node.copy(linkifyFragment(node.content, type, schema, withinCode || node.type.spec.code)));
    } else nodes.push(node);
  });
  return Fragment.fromArray(nodes);
}

function typedLink(text) {
  if (!/\s$/.test(text)) return null;
  let offset = 0;
  let found;
  for (const segment of externalLinkSegments(text)) {
    if (segment.href) found = { index: offset, label: segment.text, href: segment.href };
    offset += segment.text.length;
  }
  if (!found) return null;
  const trailing = text.slice(found.index + found.label.length);
  if (!/^[.,!?;:)\]}»”"']*\s$/.test(trailing)) return null;
  return { index: found.index, text: text.slice(found.index), data: { ...found, trailing } };
}

// A link atom occupies one document position, regardless of how long its
// clipboard label is. Tiptap's generic input matcher uses renderText(atom), so
// its string offsets cannot address this document. Read only actual text nodes
// after the last atom/line break/code span instead, using the input event's
// from/to positions (which can differ from the current selection during IME).
function textBeforeCursor($from) {
  let text = '';
  $from.parent.forEach((node, offset) => {
    if (offset >= $from.parentOffset) return;
    if (node.isText && !node.marks.some(mark => mark.type.spec.code)) {
      text += node.text.slice(0, $from.parentOffset - offset);
    } else text = '';
  });
  return text;
}

function typedLinksPlugin(editor, type) {
  let compositionTimer;
  let plugin;
  const apply = (view, from, to, text) => {
    if (!editor.isEditable || editor.isDestroyed || view.composing || (text && !/\s$/.test(text))) return false;
    const state = view.state;
    const $from = state.doc.resolve(from);
    if (!$from.parent.isTextblock || $from.parent.type.spec.code || $from.marks().some(mark => mark.type.spec.code)) return false;
    const match = typedLink(textBeforeCursor($from) + text);
    // A multi-character input must not be partially consumed by a rule.
    if (!match || match.text.length < text.length) return false;
    const start = from - (match.text.length - text.length);
    const { href, label, trailing } = match.data;
    const enter = trailing.endsWith('\n');
    const suffix = enter ? trailing.slice(0, -1) : trailing;
    const marks = state.storedMarks || $from.marks();
    const node = type.create({ href, label }, null, marks);
    const tr = state.tr.replaceWith(start, to, suffix ? [node, state.schema.text(suffix, marks)] : node);
    tr.setSelection(TextSelection.create(tr.doc, start + 1 + suffix.length));
    tr.setStoredMarks(marks);
    if (enter) {
      const commands = new CommandManager({ editor, state: createChainableState({ state, transaction: tr }) }).commands;
      if (!commands.splitBlock()) return false;
    }
    // Keep the same undo contract as Tiptap/ProseMirror input rules. Conversion
    // and paragraph insertion are one transaction, not separate history steps.
    tr.setMeta(plugin, { transform: tr, from, to, text });
    view.dispatch(tr.scrollIntoView());
    return true;
  };
  plugin = new Plugin({
    state: {
      init: () => null,
      apply: (tr, previous) => tr.getMeta(plugin) || (tr.docChanged || tr.selectionSet ? null : previous),
    },
    props: {
      handleTextInput: apply,
      handleKeyDown: (view, event) => {
        const { $cursor } = view.state.selection;
        return event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey && $cursor
          ? apply(view, $cursor.pos, $cursor.pos, '\n') : false;
      },
      handleDOMEvents: { compositionend: view => {
        clearTimeout(compositionTimer);
        compositionTimer = setTimeout(() => {
          if (editor.isDestroyed) return;
          const { $cursor } = view.state.selection;
          if ($cursor) apply(view, $cursor.pos, $cursor.pos, '');
        }, 0);
        return false;
      } },
    },
    view: () => ({ destroy: () => clearTimeout(compositionTimer) }),
    isInputRules: true,
  });
  return plugin;
}

const ExternalLinkExtension = Node.create({
  name: 'externalLink',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return { href: { default: null }, label: { default: '' } };
  },
  parseHTML() {
    return [{ tag: 'a[href]', priority: 1000, getAttrs: element => {
      const href = normalizeExternalUrl(element.getAttribute('href'));
      // Do not consume a linked image/video as a label and discard its body.
      if (!href || element.querySelector('img,picture,video,audio')) return false;
      return { href, label: element.textContent || href };
    } }];
  },
  renderHTML({ node }) {
    // No preview metadata, iframe, block status or draft-only DOM goes to the
    // server. Existing HTML storage needs only a safe anchor and its label.
    const href = normalizeExternalUrl(node.attrs.href);
    return href ? ['a', { href }, node.attrs.label || href] : ['span', {}, node.attrs.label || ''];
  },
  renderText({ node }) { return node.attrs.label || node.attrs.href || ''; },
  addNodeView() { return ReactNodeViewRenderer(ExternalLinkView); },
  addProseMirrorPlugins() {
    const type = this.type;
    return [new Plugin({ props: {
      transformPasted: slice => this.editor.state.selection.$from.parent.type.spec.code ? slice
        : new Slice(linkifyFragment(slice.content, type, this.editor.schema), slice.openStart, slice.openEnd),
    } }), typedLinksPlugin(this.editor, type)];
  },
});

export default ExternalLinkExtension;
